<template lang="pug">
div
  ValidationObserver(ref="dialogObserver")
    AppDialog.vue-time-entries-form-dialog(
      class="time-entries-form-dialog"
      :title="title"
      append-to-body
      :show="show"
      :loading="showLoading && showOnlyManualTimeRequestOption && !saving"
      @close="close"
    )
      template(#corner)
        a.dialog-faq-link.a-tooltip(
          v-tooltip="{ closeOnClick: true }"
          data-placement="bottom"
          target="_blank"
          rel="noopener noreferrer"
          title="Read the help article about this feature"
          href="https://support.hubstaff.com/how-to-add-delete-and-edit-manual-time-entries/"
        ) ?
      .form-group.align-items-center
        .div(v-if="isNew && manualTimeApprovalFeature && !showOnlyManualTimeRequestOption")
          .toggle-multiple-options.lte-three-elements.mb-20(data-toggle="buttons")
            .btn.btn-toggle-option.view-toggle(
              :class="{ active: !isWorkBreak }"
              @click.prevent="changeWorkBreakSetting(false)"
            )
              | Manual time
            .btn.btn-toggle-option.view-toggle(
              :class="{ active: isWorkBreak }"
              @click.prevent="changeWorkBreakSetting(true)"
            )
              | Work break
        .controls.input-group
          AppAvatar(
            :avatar="userAvatarUrl"
            size="medium"
          )
          .ellipsis {{ user.name }}
        .row(v-if="workBreaksEnabled && !manualTimeApprovalFeature && !showOnlyManualTimeRequestOption")
          .col-xs-12.form-col.is-centered
            label.control-label
            .form-group.boolean.optional.time_entry_work_break
              .checkbox
                label.boolean.optional
                  AppOnOffToggle.form-group.boolean.optional.time_entry_work_break(
                    v-model="isWorkBreak"
                    @input="checkForCollisions"
                  )
                    span Work break

      TimeEntriesWorkBreakPolicySelect(
        v-if="isWorkBreak"
        :work-break-policy-id="workBreakPolicyId"
        :work-break-policy-options="workBreakPolicyOptions"
        @input="updateWorkBreakPolicyId"
      )
      TimeEntriesProjectTaskSelects(
        v-show="!isWorkBreak"
        :project-id="projectId"
        :project-options="projectOptions"
        :secondary-project-options="groupedProjects"
        :task-id="taskId"
        :task-options="tasks"
        :user-id="userId"
        :project-disabled="isWeeklyCopy"
        :task-disabled="isWeeklyCopy"
        :task-required="taskRequiredForTimeEntry"
        :project-required="!isWorkBreak"
        :manual-time-request="canCreateManualTimeRequest || canExtendTimeUsingManualTimeRequest"
        :extend-time-using-manual-time-request="canExtendTimeUsingManualTimeRequest"
        @input="updateProjectTaskId"
      )
      .row
        .col-sm-12
          label.control-label {{ timeSpanLabel }}
        .row
          AppDatetimeRangePicker(
            v-model="datetimeRange"
            :min-date="minDate"
            :max-date="maxDate"
            :disable-date="isWeeklyCopy"
            :start-week-on="startWeekOn"
            :extra-picker-classes="extraPickerClasses"
            :timezone="currentZone.name"
            :move-stop-in-parallel="isNew"
            :current-zone="currentZone"
          )
      .form-group.mb-0
        TimeEntriesTimeline(
          ref="timeline"
          :date="datetimeRange.start"
          :time-entries="existingTimeEntriesForDate"
          :new-time-entry="newTimeEntryForTimeline"
          @input="updateDuration"
        )
      .add-time-warning.d-flex(v-if="addTimeWarning && !checking")
        .icon
          i.hi.hi-14.hi-info-filled
        div(v-html="addTimeWarning")
      .work-order-warning.d-flex(v-if="workOrderWarning && !checking")
        .icon
          i.hi.hi-16.hi-important
        div(v-html="workOrderWarning")
      .row(v-if="!isWorkBreak")
        .col-xs-12.form-col.is-centered
          label.control-label
          .form-group.boolean.optional.time_entry_billable
            .checkbox
              label.boolean.optional
                AppCheckbox.form-group.boolean.optional.time_entry_billable(
                  v-model="billable"
                  data-testid="time-entries-form-dialog-billable"
                )
                  span Billable
              i.hi.hi-info.a-tooltip.ml-5(
                v-tooltip="{ placement: 'right' }"
                title="Time that you bill a client for"
              )
      TimeEntriesDifferentTimeZoneWarning(:datetime-range="datetimeRange")
      WorkBreakTimeEntriesCollision(
        v-if="check.work_break"
        :check="check"
      )
      TimeEntriesCollisions(
        v-else
        :check="check"
        :is-work-break="isWorkBreak"
        :is-manual-time-request="canCreateManualTimeRequest"
        :time-entry-extended-by-manual-time-request-warning="manualTimeRequestCollidesWithTimeEntryWarning"
        :is-new="isNew"
        :editing-without-collisions-message="editingWithoutCollisionsMessage"
      )

      template(v-if="!isWorkBreak")
        ValidationProvider(
          v-slot="{ errors }"
          :rules="requiredReason"
          name="note"
        )
          .form-col(:class="{ 'has-error': errors[0] }")
            label.string.required.control-label
              | Reason
              abbr(
                v-if="reasonRequiredForTimeEntry"
                title="required"
              ) *
            AppSelect(
              ref="reason"
              v-model="note"
              name="note"
              :select2-options="getSelect2Options"
            )
            AppDialogError(:errors="errors")
        ValidationProvider(
          v-slot="{ errors }"
          :rules="requiredReasonOther"
          name="reasonOther"
        )
          .form-col(:class="{ 'has-error': errors[0] }")
            textarea.form-control.mb-20.mt-10(
              v-show="otherReasonSelected"
              v-model="reasonOther"
              name="reasonOther"
              :placeholder="requiredReasonPlaceholder"
              rows="4"
            )
            AppDialogError(:errors="errors")

        .span.mt-20
          ValidationProvider(
            v-slot="{ errors }"
            rules="max:1000"
            name="note"
          )
            div.note-panel(:class="{ 'has-error': errors[0] }")
              TimeEntriesTextarea(
                v-if="isNew && ownActivity"
                ref="note"
                v-model="workNote"
                :placeholder="workNotePlaceholder"
                name="work_note"
                label="Note"
                icon="hi-group-by"
              )
              AppDialogError(:errors="errors")
          ValidationProvider(
            v-if="canCreateManualTimeRequest"
            v-slot="{ errors }"
            ref="manualTimeRequestAttachmentProvider"
            mode="lazy"
            name="manual_time_request_attachment"
          )
            div(:class="{ 'has-error': errors[0] }")
              AppFileImport(
                show-file-types-on-label
                label="Upload file"
                resource-type="client"
                class="upload-file"
                :import-file="false"
                :file-params="manualTimeRequestAttachment"
                :file-types="manualTimeRequestAttachmentSettings?.file_types || ''"
                :mime-types="manualTimeRequestAttachmentSettings?.mime_types || ''"
                :download-file-url="manualTimeRequestAttachmentUrl"
                :organization-id="organizationId"
                :class="{ 'attachment-loading': loadingManualTimeRequestAttachment }"
                @upload-file="uploadManualTimeRequestAttachment"
                @remove-file="removeFile"
              )
              span.help-block {{ errors[0] }}
      template(#footer)
        button.btn.btn-default(@click="close") Cancel
        button.btn.btn-primary.save-button(
          :disabled="saving || denied || checking || manualTimeRequestCollidesWithTimeEntryWarning !== '' || workOrderWarning !== ''"
          @click="save"
        )
          | {{ saveButtonLabel }}
  // Confirmation dialog for unlocking time
  TimeEntriesOverrideDialog(
    :show.sync="showOverrideDialog"
    :type="type"
    :time-entry="timeEntry"
    @submit="save({ allowOverride: true })"
    @close="handleOverrideCancel"
  )
  AppConfirm(
    v-if="showProjectBudgetConfirm"
    title="Confirm changes"
    @confirm="save({ allowAboveProjectBudget: true })"
    @cancel="showProjectBudgetConfirm = false"
  )
    | {{ confirmProjectBudgetMessage }}
  //- Confirmation dialog for increasing the time limit
  AppConfirm(
    v-if="showIncreaseLimitDialog"
    title="Confirm changes"
    confirm-label="OK"
    @cancel="onIncreaseLimitConfirmCancel"
    @confirm="increaseLimitAndSave"
  )
    | {{ confirmationMessage }}
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { TimeEntryForm, TimeEntryOverrideApprovedTimesheet } from '~/mixins';
import { TimeEntriesResource } from '~/resources';
import WorkBreakTimeEntriesCollision from './WorkBreakTimeEntriesCollision.vue';
import TimeEntriesWorkBreakPolicySelect from './TimeEntriesWorkBreakPolicySelect.vue';
import { uploadToS3 } from '~/utils/s3_uploader';
import { pluralize } from '~/utils/string_utils';

export default {
  name: 'TimeEntriesFormDialog',
  components: { WorkBreakTimeEntriesCollision, TimeEntriesWorkBreakPolicySelect },
  mixins: [TimeEntryForm, TimeEntryOverrideApprovedTimesheet],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'add',
    },
    timeEntry: {
      type: Object,
      default: () => ({}),
    },
    onSave: {
      type: Function,
      default: null,
    },
    showOnlyManualTimeRequestOption: {
      type: Boolean,
      default: false,
    },
    // Opt-in: show the descriptive zone label (e.g. "Pacific, Norfolk (GMT+11:00)") instead of the
    // legacy abbreviation. Only enabled for the "Add time" dialog; everything else keeps the abbr.
    descriptiveZoneLabel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showOverrideDialog: false,
      showIncreaseLimitDialog: false,
      isWorkBreak: !!this.timeEntry.work_break_policy_id,
      denied: false,
      checking: false,
      showProjectBudgetConfirm: false,
      confirmProjectBudgetMessage: '',
      manualTimeRequestAttachment: {},
      manualTimeRequestAttachmentUrl: null,
      loadingManualTimeRequestAttachment: false,
      manualTimeRequestAttachmentAltered: false,
      reasonOther: null,
      workNotePlaceholder: 'What were you working on? (ex: Responding to customers)',
      editingWithoutCollisionsMessage: '',
      confirmationMessage: '',
      increaseLimit: false,
      limits: [],
      addTime: null,
      limitConfirmationToken: null,
      addTimeWarning: '',
      workOrderWarning: '',
      extendingTimeUsingManualTimeRequests: false,
      createdManualTimeRequestsCount: 0,
      currentUserSelected: {},
      manualTimeRequestCollidesWithTimeEntryWarning: '',
      timeAddedViaManualTimeRequests: '',
      timesheetRedirectUrl: null,
      previousValidationToken: null,
      existingTimeEntriesForDate: [],
    };
  },
  computed: {
    ...mapGetters([
      'allTimeEntries',
      'startWeekOn',
      'minDate',
      'maxDate',
      'user',
      'userAvatarUrl',
      'manualTimeApprovalFeature',
      'ownActivity',
      'currentZone',
      'groupedProjects',
      'orgProjects',
      'organizationId',
      'reasonRequiredBySettings',
      'manualTimeRequestAttachmentSettings',
      'timeEntriesForDate',
      'entryReasons',
      'isToday',
      'getProject',
      'inPlaceTimeEntries',
      'tasks',
      'workBreaksEnabled',
      'workBreakPolicies',
      'getWorkBreakPolicy',
      'showLoading',
      'addTimeDisabled',
      'newTimeEntryId',
    ]),
    hasAttachment() {
      return Object.keys(this.manualTimeRequestAttachment).length !== 0;
    },
    canAddTimeForUserValue() {
      if (!this.showOnlyManualTimeRequestOption) {
        return true;
      }

      return !this.addTimeDisabled || this.showLoading;
    },
    project() {
      return this.getProject(Number(this.projectId));
    },
    taskRequiredForTimeEntry() {
      return this.project ? this.project.tasks_required : false;
    },
    reasonRequiredForTimeEntry() {
      return this.project ? this.project.reason_required : this.reasonRequiredBySettings;
    },
    title() {
      if (this.isNew && this.showOnlyManualTimeRequestOption) {
        return 'Manual time request';
      }

      return this.isNew ? 'Add time' : 'Edit time';
    },
    isProvided() {
      return this.type === 'provided';
    },
    isAdd() {
      return this.type === 'add' || this.isWeeklyCopy;
    },
    isWeeklyCopy() {
      return this.type === 'weeklyCopy';
    },
    isNew() {
      return this.isAdd || this.isProvided;
    },
    defaultReasonSelected() {
      return !this.otherReasonSelected && this.entryReasons.includes(this.note);
    },
    requiredReason() {
      return this.reasonRequiredForTimeEntry ? 'required' : '';
    },
    requiredReasonOther() {
      return this.note.length === 0 || this.defaultReasonSelected ? '' : 'required';
    },
    requiredReasonPlaceholder() {
      const verb = this.isNew ? 'adding' : 'editing';

      return `Why are you ${verb} this time entry?`;
    },
    getSelect2Options() {
      return {
        allowClear: false,
        placeholder: this.requiredReasonPlaceholder,
        multiple: false,
        data: this.entryReasons.map((reason) => ({ id: reason, text: reason })),
        width: '100%',
      };
    },
    blockingTimeEntries() {
      return this.allTimeEntries.concat(this.inPlaceTimeEntries);
    },
    newTimeEntryForTimeline() {
      return Object.assign({}, this.timeEntry, {
        project_id: this.projectId,
        task_id: this.taskId,
        start_time: this.datetimeRange.start,
        stop_time: this.datetimeRange.stop,
        manual_time_request: this.canCreateManualTimeRequest,
      });
    },
    canCreateManualTimeRequest() {
      return this.manualTimeApprovalFeature && !this.isWorkBreak && this.isNew;
    },
    canExtendTimeUsingManualTimeRequest() {
      return this.manualTimeApprovalFeature && this.ownActivity && !this.isNew;
    },
    saveButtonLabel() {
      if (this.canCreateManualTimeRequest) {
        return 'Submit request';
      }

      if (this.denied) {
        return 'Save';
      }

      return this.saving ? 'Saving...' : 'Save';
    },
    timeSpanLabel() {
      if (!this.descriptiveZoneLabel) {
        return `Time Span (${this.currentZone.abbr})*`;
      }

      // Match the batch time entry modal's descriptive zone label (e.g. "Pacific, Norfolk (GMT+11:00)").
      // display_name is "Pacific - Norfolk"; offset is "+11:00".
      const { display_name: displayName, offset } = this.currentZone;

      return `Time Span — ${displayName.replace(' - ', ', ')} (GMT${offset}) *`;
    },
    workBreakPolicy() {
      return this.getWorkBreakPolicy(this.workBreakPolicyId);
    },
    workBreakProjectId() {
      return this.workBreakPolicy?.project_id;
    },
    workBreakPolicyOptions() {
      return this.workBreakPolicies.map((policy) => ({ text: policy.name, ...policy }));
    },
    otherReasonSelected() {
      return this.note === 'Other';
    },
    saveSuccessMessage() {
      if (this.manualTimeApprovalFeature && !this.isWorkBreak && this.isNew) {
        return 'Time entry submitted for approval';
      }
      if (this.extendingTimeUsingManualTimeRequests) {
        return `Manual time ${pluralize(this.createdManualTimeRequestsCount, 'request')} submitted`;
      }

      return 'Time entry saved';
    },
    projectOptions() {
      return this.orgProjects(this.canExtendTimeUsingManualTimeRequest);
    },
  },
  watch: {
    datetimeRange(value, oldValue) {
      if (!this.show) {
        return;
      }

      const startChanged = !value.start.isSame(oldValue.start);
      const startIsFuture = value.start.isAfter(this.now());
      const stopIsNow = moment.duration(Math.abs(oldValue.stop - this.now())).asSeconds() <= 60;

      this.duration = moment.duration(this.datetimeRange.stop - this.datetimeRange.start);
      if (this.isAdd && startChanged) {
        if (startIsFuture) {
          const oldDuration = moment.duration(oldValue.stop - oldValue.start);

          this.datetimeRange.stop = this.datetimeRange.start.clone().add(oldDuration);
        } else {
          const date = value.start.format('YYYY-MM-DD');
          const payload = {
            date,
            date_end: date,
            'filters[user]': this.userId,
          }

          TimeEntriesResource.index(
            { organizationId: this.organizationId, view: 'daily' },
            payload,
            { abortDuplicateRequests: true }
          ).then((response) => {
            this.existingTimeEntriesForDate = response.all_time_entries.map((entry) => {
              return { ...entry, start_time: moment(entry.start_time), stop_time: moment(entry.stop_time) }
            });
          }).catch((error) => {
            console.error("Error:", error);
          });

          if (this.duration < this.oneHour && stopIsNow) {
            this.datetimeRange.stop = this.datetimeRange.start.clone().add(this.oneHour);
            if (this.datetimeRange.stop.isAfter(this.now())) {
              this.datetimeRange.stop = this.now().clone().seconds(0).milliseconds(0);
            }
          }
        }
      }

      if (
        this.manualTimeApprovalFeature &&
        !this.isNew &&
        !this.isWorkBreak &&
        this.timeEntry &&
        this.timeEntry.tracked < this.duration.asSeconds()
      ) {

        const newProjectName = this.project;

        if (newProjectName?.work_order) {
          this.timeAddedViaManualTimeRequests = moment.utc((this.duration.asSeconds() - this.timeEntry.tracked) * 1000).format('H:mm')
          this.addTimeWarning = '';
          this.workOrderWarning = 'You do not have permission to increase the time span of this time entry on a work order. Manual time entries require approval. Contact your manager to modify this time entry.'
          this.extendingTimeUsingManualTimeRequests = true;
        } else {
          this.timeAddedViaManualTimeRequests = moment.utc((this.duration.asSeconds() - this.timeEntry.tracked) * 1000).format('H:mm')

          this.addTimeWarning =
            `You are extending the time span by <b>${this.timeAddedViaManualTimeRequests}</b> hours. This will be submitted as a manual time request for your manager's approval, before it is added as a time entry. If you do not want to request manual time, adjust the time span.`;
          this.extendingTimeUsingManualTimeRequests = true;
          this.workOrderWarning = '';
        }

      } else {
        this.addTimeWarning = '';
        this.workOrderWarning = '';
        this.extendingTimeUsingManualTimeRequests = false;
      }

      this.checkForCollisions();
      this.updateEditingWithoutCollisionsMessage();
    },
    show: function (value) {
      if (value) {
        this.reset();
      }
    },
    note(_, oldValue) {
      if (oldValue === 'Other') {
        this.reasonOther = null;
      }
    },
  },
  created() {
    if (this.showOnlyManualTimeRequestOption) {
      const urlParams = new URLSearchParams(window.location.search);
      const timezone = urlParams.get('time_zone');

      if (timezone) {
        this.$store.dispatch('updateSearch', { filters: { time_zone: timezone } });
      }
    }
    this.reset();
  },
  methods: {
    ...mapActions(['loadState']),
    updateProjectTaskId(value) {
      this.projectId = value[0];
      this.taskId = value[1];
      this.billable = this.project.billable;

      if (this.project.work_order && this.extendingTimeUsingManualTimeRequests) {
        this.addTimeWarning = '';
        this.workOrderWarning = 'You do not have permission to increase the time span of this time entry on a work order. Manual time entries require approval. Contact your manager to modify this time entry.'
      } else if (this.extendingTimeUsingManualTimeRequests) {
        this.addTimeWarning = `You are extending the time span by <b>${this.timeAddedViaManualTimeRequests}</b> hours. This will be submitted as a manual time request for your manager's approval, before it is added as a time entry. If you do not want to request manual time, adjust the time span.`;
        this.workOrderWarning = '';
      }

      this.checkForCollisions();
      this.updateEditingWithoutCollisionsMessage();
    },
    updateEditingWithoutCollisionsMessage() {
      if (this.timeEntry.project_id !== Number(this.projectId)) {
        const oldProjectName = this.getProject(this.timeEntry.project_id)?.text;
        const newProjectName = this.project?.text;
        const time = TimeUtils.format_duration(this.duration.asSeconds());
        const addedTime = this.duration.asSeconds() - this.timeEntry.tracked;

        this.editingWithoutCollisionsMessage = `You are editing a time entry and moving ${time} hours from ${oldProjectName} to ${newProjectName}.`;
        if (addedTime > 0 && !this.manualTimeApprovalFeature) {
          const addedTimeFormatted = TimeUtils.format_duration(addedTime);

          this.editingWithoutCollisionsMessage += ` You are adding ${addedTimeFormatted} hours as manual time.`;
        }
      } else {
        this.editingWithoutCollisionsMessage = '';
      }
    },
    updateWorkBreakPolicyId(value) {
      this.workBreakPolicyId = value;
      const policy = this.getWorkBreakPolicy(this.workBreakPolicyId);

      this.setInitialStartStopTimes(true, moment.duration(policy.duration, 'second'));
      this.checkForCollisions();
    },
    updateDuration(val) {
      const datetime = this.datetimeRange.start.clone().add(val, 's');

      this.datetimeRange = { start: this.datetimeRange.start, stop: datetime };
      this.checkForCollisions();
      this.updateEditingWithoutCollisionsMessage();
    },
    changeWorkBreakSetting(value) {
      this.isWorkBreak = value;
      this.checkForCollisions();
    },
    checkForCollisions() {
      this.check = {};

      if (this.isWorkBreak) {
        if (this.workBreakPolicyId) {
          this.dispatchCheck(this.workBreakTimeEntryParams);
        }
      } else {
        if (this.projectId) {
          this.dispatchCheck(this.timeEntryParams);
        }
      }
    },
    dispatchCheck(params) {
      const success = (response) => {
        this.checking = false; // enable the save button after checking
        if (response.alert) {
          this.$appMessage(response.alert);
        } else {
          this.validationToken = response.validation_token;
          this.check = response;
        }
        this.denied = !this.check.allowed;

        if (Object.keys(this.check.collisions).length > 0 && this.timeAddedViaManualTimeRequests !== '') {
          this.manualTimeRequestCollidesWithTimeEntryWarning = `You are extending the time span by ${this.timeAddedViaManualTimeRequests} hours. This manual time request cannot be saved as it overlaps with previously tracked time or a manual time request. To submit this request, adjust the time span.`;
          this.addTimeWarning = '';
        } else {
          this.manualTimeRequestCollidesWithTimeEntryWarning = '';
        }
      };

      if (this.canCreateManualTimeRequest || this.extendingTimeUsingManualTimeRequests || this.isWorkBreak) {
        params.check_manual_time_request_collision = true;
      }

      params.show_same_project_task_collision = this.extendingTimeUsingManualTimeRequests;
      this.checking = true; // disable the save button while checking
      this.$store
        .dispatch('checkTimeEntry', {
          id: this.showOnlyManualTimeRequestOption ? this.newTimeEntryId : this.timeEntry.id,
          params,
        })
        .then(success);
    },
    async increaseLimitAndSave() {
      this.increaseLimit = true;
      this.save();
      this.showIncreaseLimitDialog = false;
    },
    onIncreaseLimitConfirmCancel() {
      this.showIncreaseLimitDialog = false;
    },
    async save({ allowAboveProjectBudget = false, allowOverride = false } = {}) {
      const isValid = await this.$refs.dialogObserver.validate();

      if (isValid && this.canAddTimeForUserValue) {
        const success = this.success;
        const error = () => {
          this.$appMessage(this.errorMessage);
        };
        const fin = () => {
          this.saving = false;
        };
        const timeEntryParams = this.isWorkBreak ? this.workBreakTimeEntryParams : this.timeEntryParams;

        this.saving = true;
        this.showProjectBudgetConfirm = false;
        const params = {
          ...timeEntryParams,
          validation_token: this.validationToken,
          allow_above_project_budget: allowAboveProjectBudget,
          allow_override: allowOverride,
          increase_limit: this.increaseLimit,
        };

        if (this.increaseLimit) {
          params.limits = this.limits;
          params.add_time = this.addTime;
          params.limit_confirmation_token = this.limitConfirmationToken;
        }

        if (this.showOnlyManualTimeRequestOption) {
          params.manual_time_request_page = true;
        }

        params.time_entry.note = this.reasonOther || this.note;

        if (this.manualTimeRequestAttachmentAltered) {
          params.time_entry.manual_time_request_attachment = this.manualTimeRequestAttachment;
        }

        if (this.isNew) {
          this.$store.dispatch('createTimeEntry', { params }).then(success, error).then(fin);
          this.increaseLimit = false;
        } else {
          if (this.extendingTimeUsingManualTimeRequests) {
            this.$store.dispatch('updateWithManualTimeRequests', { id: this.timeEntry.id, params }).then(success, error).then(fin);
          } else {
            this.$store.dispatch('editTimeEntry', { id: this.timeEntry.id, params }).then(success, error).then(fin);
          }
        }
      }
    },
    async success(response) {
      if (response.action === 'confirm-override') {
        this.$store.dispatch('assignAssociatedRecords', {
          id: this.timeEntry.id,
          associatedRecords: response.associated_records,
        });
        this.previousValidationToken = this.validationToken;
        this.validationToken = response.validation_token;
        this.timesheetRedirectUrl = response.timesheet_redirect_url;
        this.showOverrideDialog = true;
      } else if (response.action === 'confirm-project-budget') {
        this.showProjectBudgetConfirm = true;
        this.confirmProjectBudgetMessage = response.message;
      } else if (response.action === 'error-response') {
        const errors = response.errors;

        if (response.alert?.toLowerCase().includes('attachment')) {
          const cleanMessage = response.alert.replace(/^Attachment\s*/i, '');

          errors.manual_time_request_attachment = [cleanMessage];
        }

        this.$refs.dialogObserver.setErrors(errors);
        this.$appMessage(response.alert);
      } else if (response.action === 'limit-confirmation-stale') {
        this.increaseLimit = false;
        this.limitConfirmationToken = null;
        this.$appMessage(response.alert);

        return this.save();
      } else if (response.limit_exceeded) {
        this.confirmationMessage = response.confirmation_text;
        this.showIncreaseLimitDialog = true;
        this.limits = response.limits;
        this.addTime = response.add_time;
        this.limitConfirmationToken = response.limit_confirmation_token;
      } else {
        this.createdManualTimeRequestsCount = response.manual_time_requests_count;
        if (this.isWeeklyCopy) {
          const timeEntry = this.timeEntryParams.time_entry;

          this.$store.dispatch(
            'addToInPlaceTimeEntries',
            Object.assign({}, timeEntry, {
              start_time: this.datetimeRange.start,
              stop_time: this.datetimeRange.stop,
              tracked: this.duration.asSeconds(),
            }),
          );
        } else {
          this.$store.dispatch('loadData');
        }

        if (this.showOnlyManualTimeRequestOption) {
          const url = new URL(window.location.href);

          url.searchParams.set('status', 'submitted');
          window.location.href = url.toString();
        } else {
          this.$appMessage(this.saveSuccessMessage);
        }

        if (this.timesheetRedirectUrl) {
          this.redirectToTimesheet();
        } else {
          if (this.onSave) {
            await this.onSave(response);
          }
          this.close();
        }
      }
    },
    reset() {
      if (this.isNew) {
        this.setStartStopDatetime(this.timeEntry.start_time, this.timeEntry.stop_time);
        if (this.isAdd) {
          this.setInitialStartStopTimes();
        }
        this.billable = true;
      } else {
        this.datetimeRange = { start: this.timeEntry.start_time, stop: this.timeEntry.stop_time };
        this.billable = this.timeEntry.billable;
      }

      this.$refs.note && this.$refs.note.hideTextArea();

      this.userId = this.user.id;
      this.projectId = this.timeEntry.project_id;
      this.taskId = this.timeEntry.task_id;
      this.workNote = '';
      this.note = '';
      this.reasonOther = null;
      this.check = {};
      this.isWorkBreak = !!this.timeEntry.work_break_policy_id;
      this.workBreakPolicyId = this.timeEntry.work_break_policy_id;
      this.editingWithoutCollisionsMessage = '';
      this.increaseLimit = false;
      this.addTimeWarning = '';
      this.workOrderWarning = '';
      this.currentUserSelected = this.user;
      this.manualTimeRequestAttachment = {};
      this.manualTimeRequestAttachmentUrl = null;
      this.manualTimeRequestAttachmentAltered = false;
      this.loadingManualTimeRequestAttachment = false;
      this.manualTimeRequestCollidesWithTimeEntryWarning = '';
      this.timeAddedViaManualTimeRequests = '';
      this.extendingTimeUsingManualTimeRequests = false;
      this.timesheetRedirectUrl = null;
      this.previousValidationToken = null;
      this.existingTimeEntriesForDate = [];
    },
    close() {
      this.reset();
      this.$refs.dialogObserver?.reset();
      this.$emit('close');
    },
    fetcher(payload) {
      return TimeEntriesResource.members({ organization_id: this.organizationId }, payload, {
        abortReload: true,
      });
    },
    async memberChanged(user) {
      if (user) {
        const urlParams = new URLSearchParams(window.location.search);
        const timezone = urlParams.get('time_zone');
        const filters = { user: user.id };

        if (timezone) {
          filters.time_zone = timezone;
        }
        await this.$store.dispatch('loadData', { filters });
        this.reset();
        this.$refs.dialogObserver.reset();
        this.currentUserSelected = user;
      }
    },
    async uploadManualTimeRequestAttachment(file) {
      this.manualTimeRequestAttachmentUrl = null;
      this.$refs.manualTimeRequestAttachmentProvider.reset();
      this.manualTimeRequestAttachment = {};

      try {
        return await new Promise((resolve, reject) => {
          this.loadingManualTimeRequestAttachment = true;
          uploadToS3(this.manualTimeRequestAttachmentSettings.presign_path, file, resolve, reject);
        }).then((response) => {
          this.manualTimeRequestAttachment = {
            id: response.fields.key.match(/cache\/(.+)$/)[1],
            storage: 'cache',
            metadata: {
              size: file.size,
              filename: file.name,
              mime_type: file.type,
            },
          };
          this.manualTimeRequestAttachmentAltered = true;
        });
      } catch (e) {
        const responseXML = e.responseXML;

        if (responseXML && responseXML.documentElement.firstChild.innerHTML === 'EntityTooLarge') {
          const errorMessage = `File is too large. (max is ${this.manualTimeRequestAttachmentSettings.max_size} MB)`;

          this.$refs.manualTimeRequestAttachmentProvider.setErrors([errorMessage]);
          throw new Error(errorMessage);
        }
        else {
          const errorMessage = 'Unexpected error. Please try again.';

          this.$refs.manualTimeRequestAttachmentProvider.setErrors([errorMessage]);
          throw new Error(errorMessage);
        }
      }
      finally {
        this.loadingManualTimeRequestAttachment = false;
      }
    },
    removeFile() {
      this.manualTimeRequestAttachmentUrl = null;
      this.manualTimeRequestAttachment = {};
      this.manualTimeRequestAttachmentAltered = true;
      this.$refs.manualTimeRequestAttachmentProvider.reset();
    },
  },
};
</script>
